import axios, { AxiosPromise, AxiosResponse } from 'axios';
import { ApiResponse } from './responses';

const successCodes = [200, 201, 204];

type GivingApiBody = {
    response?: {
        code?: number;
        errorMessage?: string;
        errors?: Record<string, unknown>;
    };
};

const getGivingError = (
    data: unknown,
): {
    code?: number;
    message?: string;
    errors?: Record<string, unknown>;
} => {
    const givingBody = data as GivingApiBody;
    return {
        code: givingBody.response?.code,
        message: givingBody.response?.errorMessage,
        errors: givingBody.response?.errors,
    };
};

export const makeApiRequest = async <T>(
    httpPromise: AxiosPromise,
): Promise<ApiResponse<T>> => {
    let result: ApiResponse<T>;
    let response: AxiosResponse<unknown>;

    try {
        response = await httpPromise;

        if (successCodes.includes(response.status)) {
            const body = response.data as T;
            const givingError = getGivingError(response.data);
            const responseCode = givingError.code;
            if (
                responseCode !== undefined &&
                !successCodes.includes(responseCode)
            ) {
                result = {
                    success: false,
                    error: {
                        isAxiosError: false,
                        status: responseCode ?? 500,
                        errors: givingError.errors,
                        message: givingError.message || response.statusText,
                        name: String(responseCode ?? 500),
                    },
                };
                return result;
            }
            result = {
                success: true,
                data: body,
            };
        } else {
            //eslint-disable-next-line
            const resp = response as any;
            const givingError = getGivingError(resp.data);
            const status = givingError.code ?? response.status;
            const message =
                givingError.message ||
                (resp ? (resp.data ? resp.data.message : '') : resp.statusText);
            result = {
                success: false,
                error: {
                    isAxiosError: false,
                    status,
                    errors: givingError.errors || resp?.data?.errors,
                    message: message,
                    name: status.toString(),
                },
            };
        }
    } catch (err) {
        //eslint-disable-next-line
        const error = err as any;
        response = error;

        if (error.response) {
            const resp = error.response;
            const givingError = getGivingError(resp.data);
            const status = givingError.code ?? error.response.status;
            const message =
                givingError.message || resp.data?.message || resp.statusText;
            result = {
                success: false,
                error: {
                    isAxiosError: false,
                    status,
                    message: message,
                    errors: givingError.errors || resp?.data?.errors,
                    name: status.toString(),
                },
            };
        } else if (axios.isCancel(error)) {
            result = {
                success: false,
                error: {
                    isAxiosError: true,
                    status: 499,
                    message: 'Request cancelled',
                    name: '499',
                },
            };
        } else {
            result = {
                success: false,
                error: {
                    isAxiosError: true,
                    status: 500,
                    message: 'ERR_CONNECTION_REFUSED. Possible network problem',
                    name: '500',
                },
            };
        }
    }
    return result;
};
